import { createRoute, Link } from '@tanstack/react-router'
import { rootRoute } from './__root'
import {
  ArrowUpRightIcon,
  SmartphoneIcon,
  GlobeIcon,
  UsersIcon,
} from '../components/Icons'

export const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomeComponent,
})

const TRUCO_URL = 'https://trucogame.com/'
const JOGA_TRUCO_URL = 'https://jogatruco.com/'

function HomeComponent() {
  return (
    <div className="page home-page">
      {/* Hero */}
      <section className="hero">
        <div className="container hero-grid">
          <div className="hero-content">
            <span className="eyebrow">game studio · buenos aires</span>
            <h1 className="hero-title">
              We build games people{' '}
              <br />
              <span className="highlight">actually keep playing.</span>
            </h1>
            <p className="hero-subtitle">
              Cloudymedia is an independent studio focused on mobile, web and
              social games. Our flagship title, Gran Truco Argentino, is played
              by more than two million people, and Joga Truco brings Truco
              Paulista and Truco Mineiro to Brazil.
            </p>
            <div className="hero-cta">
              <a
                href={TRUCO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Play Gran Truco
                <ArrowUpRightIcon />
              </a>
              <Link to="/about" className="btn btn-secondary">
                About the studio
              </Link>
            </div>
            <div className="hero-meta">
              <span>iOS · Android · Web</span>
              <span>Online multiplayer</span>
              <span>Est. Argentina</span>
            </div>
          </div>

          <aside className="panel" aria-label="Gran Truco Argentino at a glance">
            <div className="panel-bar">
              <span className="panel-dots">
                <i />
                <i />
                <i />
              </span>
              <span>gran-truco-argentino</span>
              <span className="status">
                <i className="status-dot" />
                live
              </span>
            </div>
            <div className="panel-body">
              <div className="panel-row">
                <span className="key">product</span>
                <span className="val">Gran Truco Argentino</span>
              </div>
              <div className="panel-row">
                <span className="key">category</span>
                <span className="val">Card game · Multiplayer</span>
              </div>
              <div className="panel-row">
                <span className="key">rating</span>
                <span className="val accent">4.9 / 5.0</span>
              </div>

              <div className="panel-divider" />

              <div className="panel-metrics">
                <div className="metric">
                  <span className="metric-value">2M+</span>
                  <span className="metric-label">players</span>
                </div>
                <div className="metric">
                  <span className="metric-value">100K+</span>
                  <span className="metric-label">games / day</span>
                </div>
                <div className="metric">
                  <span className="metric-value">3</span>
                  <span className="metric-label">platforms</span>
                </div>
              </div>

              <div className="tag-list">
                <span className="tag accent">ios</span>
                <span className="tag accent">android</span>
                <span className="tag accent">web</span>
                <span className="tag">realtime</span>
                <span className="tag">social</span>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Stats */}
      <section className="stats-strip">
        <div className="container stats-grid">
          <div className="stat-item">
            <span className="stat-number">2M+</span>
            <span className="stat-label">Registered players</span>
            <span className="stat-note">across all platforms</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">4.9</span>
            <span className="stat-label">Average store rating</span>
            <span className="stat-note">out of 5.0</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">100K+</span>
            <span className="stat-label">Games played daily</span>
            <span className="stat-note">and counting</span>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">what we do</span>
            <h2 className="section-title">
              Three platforms, one codebase, zero compromises.
            </h2>
            <p className="section-lead">
              We design, build and operate our own games end to end, from
              gameplay and matchmaking to store releases and live operations.
            </p>
          </div>

          <div className="card-grid">
            <article className="card">
              <div className="card-icon">
                <SmartphoneIcon />
              </div>
              <span className="card-index">01</span>
              <h3 className="card-title">Mobile games</h3>
              <p className="card-text">
                Native-feeling experiences on iOS and Android, published and
                maintained on both app stores with regular updates.
              </p>
            </article>

            <article className="card">
              <div className="card-icon">
                <GlobeIcon />
              </div>
              <span className="card-index">02</span>
              <h3 className="card-title">Web games</h3>
              <p className="card-text">
                Instant play in the browser. No installs, no friction, the same
                game and the same players as on mobile.
              </p>
            </article>

            <article className="card">
              <div className="card-icon">
                <UsersIcon />
              </div>
              <span className="card-index">03</span>
              <h3 className="card-title">Social &amp; multiplayer</h3>
              <p className="card-text">
                Realtime online matches, friends, and communities built around
                the games people already love to play together.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="section">
        <div className="container">
          <div className="feature">
            <div className="feature-content">
              <span className="eyebrow">featured title</span>
              <h2 className="feature-title">Gran Truco Argentino</h2>
              <p className="feature-text">
                The most authentic digital version of Truco, Argentina's
                national card game. Challenge friends, play against people from
                around the world, and enjoy the real rules, the real bluffing
                and the real banter.
              </p>
              <div className="feature-actions">
                <a
                  href={TRUCO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  Play now
                  <ArrowUpRightIcon />
                </a>
                <Link to="/games" className="btn btn-secondary">
                  Game details
                </Link>
              </div>
            </div>

            <div className="spec-list" aria-label="Game specifications">
              <div className="spec-row">
                <span className="spec-key">title</span>
                <span className="spec-val">Gran Truco Argentino</span>
              </div>
              <div className="spec-row">
                <span className="spec-key">genre</span>
                <span className="spec-val">Card · Strategy</span>
              </div>
              <div className="spec-row">
                <span className="spec-key">mode</span>
                <span className="spec-val">Online multiplayer</span>
              </div>
              <div className="spec-row">
                <span className="spec-key">platforms</span>
                <span className="spec-val">iOS, Android, Web</span>
              </div>
              <div className="spec-row">
                <span className="spec-key">status</span>
                <span className="spec-val accent">live · 2M+ players</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* New release */}
      <section className="section" lang="pt-BR">
        <div className="container">
          <div className="feature">
            <div className="feature-content">
              <span className="eyebrow">lançamento · brasil</span>
              <h2 className="feature-title">
                Joga Truco: Truco Paulista e Mineiro
              </h2>
              <p className="feature-text">
                Jogue truco online grátis com amigos ou contra jogadores de todo
                o Brasil. Truco Paulista com vira e manilha, Truco Mineiro com
                manilhas fixas, no celular ou no navegador.
              </p>
              <div className="feature-actions">
                <a
                  href={JOGA_TRUCO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                  title="Jogar truco online no navegador"
                >
                  Jogar truco online
                  <ArrowUpRightIcon />
                </a>
                <Link
                  to="/games"
                  hash="joga-truco"
                  className="btn btn-secondary"
                >
                  Baixar o app
                </Link>
              </div>
            </div>

            <div className="spec-list" aria-label="Especificações do jogo">
              <div className="spec-row">
                <span className="spec-key">title</span>
                <span className="spec-val">Joga Truco</span>
              </div>
              <div className="spec-row">
                <span className="spec-key">variants</span>
                <span className="spec-val">Truco Paulista · Truco Mineiro</span>
              </div>
              <div className="spec-row">
                <span className="spec-key">mode</span>
                <span className="spec-val">Multiplayer online</span>
              </div>
              <div className="spec-row">
                <span className="spec-key">platforms</span>
                <span className="spec-val">iOS, Android, Web</span>
              </div>
              <div className="spec-row">
                <span className="spec-key">status</span>
                <span className="spec-val accent">lançamento</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
